import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "../firebase";
import { foundingCreatorFields, isFoundingPeriodActive } from "../utils/foundingPeriod";

async function findUserByEmail(email) {
  const clean = email.trim().toLowerCase();
  const snap = await getDocs(
    query(collection(db, "users"), where("email", "==", clean), limit(1))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

async function mergeApprovedUserProfile(email, fields) {
  const user = await findUserByEmail(email);
  if (!user?.id) return null;

  await updateDoc(doc(db, "users", user.id), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
  return user.id;
}

/**
 * Approve a fan waitlist entry — sets waitlist fields and syncs users/{uid} if account exists.
 * Triggers onFanApproved Cloud Function for approval email.
 */
export async function approveFanWaitlistEntry(email) {
  const cleanEmail = email.trim().toLowerCase();
  const waitlistRef = doc(db, "waitlist_fans", cleanEmail);
  const waitlistSnap = await getDoc(waitlistRef);
  if (!waitlistSnap.exists()) {
    throw new Error("Fan waitlist entry not found.");
  }

  const approvedAt = serverTimestamp();
  const waitlistPayload = {
    approved: true,
    approvedAt,
    role: "fan",
    isCreator: false,
  };

  await updateDoc(waitlistRef, waitlistPayload);

  const userPayload = {
    isCreator: false,
    role: "fan",
    approved: true,
    approvedAt,
  };

  return mergeApprovedUserProfile(cleanEmail, userPayload);
}

/**
 * Approve a creator waitlist entry — sets waitlist fields and syncs users/{uid} if account exists.
 * Triggers onCreatorApproved Cloud Function (referral code + approval email).
 */
export async function approveCreatorWaitlistEntry(email) {
  const cleanEmail = email.trim().toLowerCase();
  const waitlistRef = doc(db, "waitlist_creators", cleanEmail);
  const waitlistSnap = await getDoc(waitlistRef);
  if (!waitlistSnap.exists()) {
    throw new Error("Creator waitlist entry not found.");
  }

  const approvedAt = serverTimestamp();
  const founding = isFoundingPeriodActive();

  const waitlistPayload = {
    approved: true,
    approvedAt,
    role: "creator",
    isCreator: true,
    ...(founding ? foundingCreatorFields() : {}),
  };

  await updateDoc(waitlistRef, waitlistPayload);

  const userPayload = {
    isCreator: true,
    role: "creator",
    approved: true,
    approvedAt,
    creatorOnboardingComplete: false,
    ...(founding ? foundingCreatorFields() : {}),
  };

  return mergeApprovedUserProfile(cleanEmail, userPayload);
}

/**
 * Apply waitlist approval fields when a user signs up with a matching email.
 */
export async function applyWaitlistApprovalToNewUser(uid, email) {
  if (!uid || !email) return null;

  const cleanEmail = email.trim().toLowerCase();
  const [fanSnap, creatorSnap] = await Promise.all([
    getDoc(doc(db, "waitlist_fans", cleanEmail)),
    getDoc(doc(db, "waitlist_creators", cleanEmail)),
  ]);

  const fan = fanSnap.exists() && fanSnap.data().approved ? fanSnap.data() : null;
  const creator = creatorSnap.exists() && creatorSnap.data().approved ? creatorSnap.data() : null;

  if (!fan && !creator) return null;

  const isCreator = Boolean(creator);
  const source = creator || fan;
  const founding = isCreator && isFoundingPeriodActive();

  const profileFields = {
    isCreator,
    role: isCreator ? "creator" : "fan",
    approved: true,
    approvedAt: source.approvedAt || serverTimestamp(),
    email: cleanEmail,
    ...(isCreator && founding ? foundingCreatorFields() : {}),
    ...(isCreator && source.niche ? { niche: Array.isArray(source.niche) ? source.niche : [source.niche] } : {}),
    ...(isCreator && source.handle ? { handle: source.handle, username: source.handle } : {}),
  };

  await updateDoc(doc(db, "users", uid), {
    ...profileFields,
    updatedAt: serverTimestamp(),
  });

  return profileFields;
}

/** Backfill isCreator on all seed user documents. */
export async function patchSeedUserIsCreatorFlags() {
  const snap = await getDocs(
    query(collection(db, "users"), where("isSeedData", "==", true), limit(500))
  );

  const batch = writeBatch(db);
  let count = 0;

  snap.docs.forEach((d) => {
    const data = d.data();
    const shouldBeCreator = data.role === "creator" || Boolean(data.foundingCreator);
    const next = shouldBeCreator
      ? { isCreator: true, role: "creator" }
      : { isCreator: false, role: data.role === "superadmin" ? data.role : "fan" };

    if (data.isCreator !== next.isCreator || data.role !== next.role) {
      batch.update(d.ref, { ...next, updatedAt: serverTimestamp() });
      count += 1;
    }
  });

  if (count > 0) await batch.commit();
  return count;
}
